/**
 * Canonical request-body sanitizer for the Atlas API clients.
 *
 * The typed client serializes bodies via `serializeDates` BEFORE axios. In Firestore-read
 * mode, entity objects can carry a live native @react-native-firebase `DocumentReference`
 * (`x.ref = doc.ref`), which is cyclical on iOS. The original `serializeDates` recursed with
 * no cycle guard, so such a body recursed until the stack overflowed
 * ("RangeError: Maximum call stack size exceeded" — Sentry QARASAY-KASSA-26F/26G) before axios
 * ever ran. The legacy `apiClient.js` instance hit the same class as a JSON.stringify
 * "cyclical structure in JSON object" (26A/26B).
 *
 * This is cycle-safe (WeakSet seen-set), strips `ref` keys and DocumentReference-shaped values
 * (the backend ignores them — it resolves IDs), and passes opaque body types (FormData/Blob)
 * through untouched. For a normal DTO it produces exactly what `serializeDates` did (Date→ISO,
 * everything else identical), so request-schema validation is unaffected.
 *
 * @param data request body
 * @param opts.serializeDates true (default) → Date becomes an ISO string (typed client);
 *   false → Date objects are preserved (legacy apiClient interceptor, where axios serializes).
 */
export function sanitizeRequestBody<T>(data: T, opts?: { serializeDates?: boolean }): T {
  const serializeDates = opts?.serializeDates ?? true
  return sanitize(data, new WeakSet(), serializeDates) as T
}

/** A value shaped like a Firestore DocumentReference (native or JS SDK). */
function isDocumentReference(value: object): boolean {
  const ctorName = value.constructor && value.constructor.name ? value.constructor.name : ''
  if (ctorName.includes('Reference')) return true
  return 'firestore' in value && 'path' in value && typeof (value as { path: unknown }).path === 'string'
}

/**
 * Body types that must NOT be deep-cloned — a naive clone drops their internals (FormData has no
 * enumerable own-keys → would become {}), silently breaking file/image uploads.
 */
function isOpaqueBodyType(value: object): boolean {
  const tag = Object.prototype.toString.call(value)
  if (
    tag === '[object FormData]' ||
    tag === '[object Blob]' ||
    tag === '[object File]' ||
    tag === '[object ArrayBuffer]' ||
    tag === '[object URLSearchParams]' ||
    ArrayBuffer.isView(value) // typed arrays / DataView
  ) {
    return true
  }
  // React Native's FormData/Blob polyfills DON'T set Symbol.toStringTag, so the tag above is
  // '[object Object]' and a multipart upload would be deep-cloned to {} — the file is silently
  // dropped (empty message). This only breaks on native; web's FormData has the proper tag.
  // Catch the RN case by identity (instanceof the global) and by duck-typing (RN FormData exposes
  // getParts(); Blob exposes slice() + size). (ATL: iOS support-chat image upload sent empty.)
  const g = globalThis as Record<string, unknown>
  const isInstanceOfGlobal = (name: string): boolean =>
    typeof g[name] === 'function' && value instanceof (g[name] as new (...args: unknown[]) => unknown)
  if (
    isInstanceOfGlobal('FormData') ||
    isInstanceOfGlobal('Blob') ||
    isInstanceOfGlobal('File') ||
    isInstanceOfGlobal('URLSearchParams')
  ) {
    return true
  }
  const v = value as { append?: unknown; getParts?: unknown }
  if (typeof v.append === 'function' && typeof v.getParts === 'function') {
    return true // React Native FormData (has getParts())
  }
  return false
}

function sanitize(value: unknown, seen: WeakSet<object>, serializeDates: boolean): unknown {
  if (value === null || value === undefined) return value
  if (typeof value === 'function') return undefined
  if (value instanceof Date) return serializeDates ? value.toISOString() : value
  if (typeof value !== 'object') return value

  const obj = value as object
  if (isOpaqueBodyType(obj)) return obj
  if (isDocumentReference(obj)) return undefined

  // Cycle guard: a value already on the current path is a back-edge — drop it.
  if (seen.has(obj)) return undefined
  seen.add(obj)

  let result: unknown
  if (Array.isArray(value)) {
    result = value.map((item) => sanitize(item, seen, serializeDates))
  } else {
    const out: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      if (key === 'ref') continue // strip the Firestore-ref convention key
      const sanitized = sanitize(val, seen, serializeDates)
      if (sanitized !== undefined) out[key] = sanitized
    }
    result = out
  }

  // Allow the same object to reappear on a sibling branch (DAG, not a cycle).
  seen.delete(obj)
  return result
}
